// eslint-disable-next-line no-restricted-imports
import { type SortDescriptor } from "@progress/kendo-data-query";
import { message } from "antd";
import { useCallback, useContext, useEffect, useState } from "react";
import { type RouteComponentProps } from "react-router";

import { billsApprovalQueueGridPostV2 } from "@btnet/services-btnet/handlers/bills/bills";

import { BdsIconArrowLeft, BdsLink } from "@buildertrend/components";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { useWebviewContext } from "helpers/webview/WebviewContext";

import { type IBaseEntity } from "types/apiResponse/apiResponse";

import { showAPIErrorMessage } from "utilities/apiHandler";
import { isGridErrorMessageApiError, showGridErrorMessage } from "utilities/grid/grid";
import {
    isCancelledGridRequestError,
    useCancellableGridRequest,
} from "utilities/grid/useCancellableGridRequest";
import { type ListEntityType, PagingData } from "utilities/list/list.types";
import { routes } from "utilities/routes";

import BdsListPageHeader from "commonComponents/bdsWrappers/BdsListPageHeader/BdsListPageHeader";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTListHeader } from "commonComponents/btWrappers/BTListHeader/BTListHeader";
import { BTListLayoutWhitespace } from "commonComponents/btWrappers/BTListLayout/BTListLayout";
import {
    BTListLayoutBDS,
    ListSection,
} from "commonComponents/btWrappers/BTListLayoutBDS/BTListLayoutBDS";
import { BTPagination } from "commonComponents/btWrappers/BTPagination/BTPagination";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { getBillColumnImplementations } from "commonComponents/utilities/Grid/bill/billColumnImplementations";
import { BillLinkAbsoluteContext } from "commonComponents/utilities/Grid/bill/billLinkMode";
import { standardColumnImplementations } from "commonComponents/utilities/Grid/common/columnImplementations/standardColumnImplementations";
import {
    GridContainer,
    gridStateManager,
    initialGridState,
} from "commonComponents/utilities/Grid/GridContainer";
import {
    type IGridRowData,
    type IGridState,
    type IGroupedRowData,
    isGridRowData,
} from "commonComponents/utilities/Grid/GridContainer.types";
import { ListActionBar } from "commonComponents/utilities/ListActionBar/ListActionBar";

import { type BillDocumentType } from "entity/bill/Bill/Bill.api.types";
import { RouteBill } from "entity/bill/Bill/RouteBill";
import { getBillToastMessage } from "entity/bill/common/getBillToastMessage";
import { getBillViewModePreference } from "entity/bill/common/resolveBillViewMode";
import { performBulkApprove } from "entity/bill/shared/performBulkApprove";

import { type IBillRowSelection } from "../BillList/utils/billFloatingBarActionEligibility";
import { type BillApprovalQueueFormActions, BillGridEntity } from "./BillApprovalQueue.api.types";
import { BillApprovalQueueEmptyState } from "./BillApprovalQueueEmptyState/BillApprovalQueueEmptyState";
import { BillApprovalQueueFloatingActionBar } from "./BillApprovalQueueFloatingActionBar/BillApprovalQueueFloatingActionBar";

// Find the bill that follows the just-approved bill in the visible queue.
// Returns undefined when the approved bill is the last item or is not in the list,
// which signals to callers that the modal should close instead of advancing.
// Exported so the lookup can be unit-tested without rendering the queue.
export function getNextBillInQueue(
    rows: (IGridRowData | IGroupedRowData)[],
    approvedBillId: number,
): { id: number; jobId: number } | undefined {
    const flatRows = rows.filter(isGridRowData);
    const approvedIndex = flatRows.findIndex((row) => row.id === approvedBillId);
    if (approvedIndex < 0 || approvedIndex >= flatRows.length - 1) {
        return undefined;
    }
    const next = flatRows[approvedIndex + 1];
    if (next.jobId === undefined) {
        return undefined;
    }
    return { id: next.id, jobId: next.jobId };
}

// Build a PagingData targeting a specific page, preserving page size/total.
function clonePagingForPage(source: PagingData, page: number): PagingData {
    const paging = new PagingData(source.pageSize);
    paging.currentPage = page;
    paging.firstRow = (page - 1) * source.pageSize + 1;
    paging.lastRow = page * source.pageSize;
    paging.total = source.total;
    return paging;
}

// Owns routing and the toast after approve, since only this helper knows
// whether a next bill will open. Three cases:
//   1. Next bill in current rows → navigate to it, reload page.
//   2. Approved bill was last visible but more exist → reload and open the row
//      that slid into the tail slot (detected by reloaded page still being full).
//   3. No bills remain past the approved one → reload to the highest valid page
//      and close the modal.
export async function handleApproveAndNextNavigation(
    rows: (IGridRowData | IGroupedRowData)[],
    approvedBillId: number,
    pagingData: PagingData,
    parentRoute: string,
    history: { replace: (path: string) => void },
    loadPage: (paging: PagingData) => Promise<BillGridEntity | undefined>,
    onClose: () => Promise<void>,
    documentType: BillDocumentType | undefined,
): Promise<void> {
    const next = getNextBillInQueue(rows, approvedBillId);

    if (next) {
        history.replace(parentRoute + routes.bill.getDetailsLink(next.id, next.jobId));
        void loadPage(pagingData);
        void message.success(getBillToastMessage("approvedAndNext", documentType), 5);
        return;
    }

    const newTotal = Math.max(0, pagingData.total - 1);
    const newLastPage = newTotal === 0 ? 1 : Math.ceil(newTotal / pagingData.pageSize);
    const targetPage = Math.min(pagingData.currentPage, newLastPage);
    const targetPaging = clonePagingForPage(pagingData, targetPage);
    const loaded = await loadPage(targetPaging);

    if (targetPage === pagingData.currentPage && newTotal > 0) {
        const flatRows = loaded?.data.filter(isGridRowData) ?? [];
        if (flatRows.length >= pagingData.pageSize) {
            const slidInRow = flatRows[flatRows.length - 1];
            if (slidInRow.jobId !== undefined) {
                history.replace(
                    parentRoute + routes.bill.getDetailsLink(slidInRow.id, slidInRow.jobId),
                );
                void message.success(getBillToastMessage("approvedAndNext", documentType), 5);
                return;
            }
        }
    }

    void message.success(getBillToastMessage("approved", documentType), 5);
    void onClose();
}

// Column IDs to display in the approval queue
// These map to BillsDynamicGridColumnTypes enum in Global.Core\Enums\Bills\Bills.cs
const APPROVAL_QUEUE_VISIBLE_COLUMNS = [
    "6", // Job
    "27", // BillNumber
    "1", // Title
    "8", // PayTo
    "7", // Amount
    "3", // InvoicedDate
    "39", // Approver
    "33", // CostCodes
    "34", // VarianceCodes
    "35", // RelatedPOs
];

const APPROVAL_QUEUE_PAGE_SIZE = 25;

interface IBillApprovalQueueProps extends RouteComponentProps {
    entityType: ListEntityType;
    isWebView?: boolean;
}

/**
 * Bill Approval Queue component.
 * Displays bills that are awaiting approval by the current user.
 * This is a simplified version of BillList with:
 * - No filter drawer
 * - No job picker UI
 * - No Purchase Orders / Bills tabs
 * - Hardcoded filters (Status=InReview, Approver=current user, AwaitingApprovalOnly=true)
 * - Restricted column set
 */
export const BillApprovalQueueInternal = (props: IBillApprovalQueueProps) => {
    const { entityType, history, location, match } = props;
    const webview = useWebviewContext();
    const { getSignal } = useCancellableGridRequest();
    const builderInfo = useContext(BuilderInfoContext);
    const newBTListHeader = !!builderInfo?.flags?.bdsContentHeaderListPageReplacement;

    const [gridState, setGridState] = useState<IGridState>(initialGridState);
    const [gridEntity, setGridEntity] = useState<BillGridEntity | undefined>(undefined);
    const [actionBeingPerformed, setActionBeingPerformed] =
        useState<BillApprovalQueueFormActions>(undefined);

    const loadList = useCallback(
        async (
            pagingData: PagingData,
            sortingData: SortDescriptor[],
        ): Promise<BillGridEntity | undefined> => {
            const signal = getSignal();
            const sortColumn = sortingData.length > 0 ? sortingData[0].field : "0";
            const sortDirection = sortingData.length > 0 ? (sortingData[0].dir ?? "asc") : "asc";

            const apiResponse = await billsApprovalQueueGridPostV2(
                {
                    pagingData,
                    sortColumn,
                    sortDirection,
                },
                undefined,
                signal,
            );

            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
            const gridResponse = new BillGridEntity(apiResponse.data as any);

            gridResponse.transformEntity(
                [...standardColumnImplementations, ...getBillColumnImplementations()],
                undefined,
                undefined,
            );

            // Order columns to match APPROVAL_QUEUE_VISIBLE_COLUMNS and update their order property
            gridResponse.columnsList = APPROVAL_QUEUE_VISIBLE_COLUMNS.map((id, index) => {
                const col = gridResponse.columnsList.find((col) => col.id === id);
                if (col) {
                    col.order = index;
                }
                return col;
            }).filter((col) => col !== undefined);

            gridResponse.columnsListAll = APPROVAL_QUEUE_VISIBLE_COLUMNS.map((id, index) => {
                const col = gridResponse.columnsListAll.find((col) => col.id === id);
                if (col) {
                    col.order = index;
                }
                return col;
            }).filter((col) => col !== undefined);

            return gridResponse;
        },
        [getSignal],
    );

    const loadListIntoState = useCallback(
        async (
            updatedPagingData?: PagingData,
            updatedSortData?: SortDescriptor[],
        ): Promise<BillGridEntity | undefined> => {
            try {
                const pagingData =
                    updatedPagingData ??
                    gridEntity?.pagingData ??
                    new PagingData(APPROVAL_QUEUE_PAGE_SIZE);
                const sortingData = updatedSortData ?? gridState.sortingData;

                const loadedGridEntity = await loadList(pagingData, sortingData);
                if (!loadedGridEntity) {
                    return undefined;
                }

                setGridState((prevState) => ({
                    ...prevState,
                    dataState: "loaded",
                    data: loadedGridEntity.data,
                    columnsList: loadedGridEntity.columnsList,
                    sortingData: sortingData,
                    footerData: loadedGridEntity.footerData,
                }));
                setGridEntity(loadedGridEntity);
                return loadedGridEntity;
            } catch (error) {
                if (isCancelledGridRequestError(error)) return;

                if (isGridErrorMessageApiError(error)) {
                    showGridErrorMessage(error);
                } else {
                    showAPIErrorMessage(error);
                }
                setGridState((prevState) => gridStateManager.onError(prevState));
                return undefined;
            }
        },
        [gridEntity?.pagingData, gridState.sortingData, loadList],
    );

    const handleReload = useCallback(async () => {
        await loadListIntoState();
    }, [loadListIntoState]);

    const handleBackToBills = useCallback(() => {
        if (webview) {
            // Navigate directly to Bills List (not just back in history)
            history.push(routes.bill.getWebviewListLink(webview));
        } else {
            history.push(routes.bill.getListLink());
        }
    }, [history, webview]);

    const handleSort = useCallback(
        async (sortingData: SortDescriptor[]) => {
            await loadListIntoState(new PagingData(APPROVAL_QUEUE_PAGE_SIZE), sortingData);
        },
        [loadListIntoState],
    );

    const handlePageChange = useCallback(
        async (updatedPagingData: PagingData) => {
            await loadListIntoState(updatedPagingData);
        },
        [loadListIntoState],
    );

    const handleBeforeClose = useCallback(async () => {
        history.replace(match.url);
        await handleReload();
    }, [history, match.url, handleReload]);

    const handleApproveAndNext = useCallback(
        (approvedBillId: number, documentType: BillDocumentType | undefined) => {
            if (!gridEntity?.pagingData) {
                return;
            }
            void handleApproveAndNextNavigation(
                gridEntity.data,
                approvedBillId,
                gridEntity.pagingData,
                match.url,
                history,
                loadListIntoState,
                handleBeforeClose,
                documentType,
            );
        },
        [
            gridEntity?.data,
            gridEntity?.pagingData,
            handleBeforeClose,
            history,
            loadListIntoState,
            match.url,
        ],
    );

    const handleBulkApprove = useCallback(
        async (selectedBills: IBaseEntity[], onDeselect: () => void) => {
            setActionBeingPerformed("checkedAction");
            // Grid selection trims rows to {id, builderId, jobId}, so re-hydrate each selected row
            // from the loaded grid data so performBulkApprove can read purchaseOrderPaymentStatus
            // to derive the selection's document type for toast wording.
            const rowById = new Map<number, IBillRowSelection>(
                (gridState.data ?? [])
                    .filter(isGridRowData)
                    .map((row) => [row.id, row as IBillRowSelection]),
            );
            const selectedRows: IBillRowSelection[] = selectedBills.map(
                (b) => rowById.get(b.id) ?? (b as IBillRowSelection),
            );
            await performBulkApprove(selectedRows);
            onDeselect();
            await handleReload();
            setActionBeingPerformed(undefined);
        },
        [handleReload, gridState.data],
    );

    const renderFab = useCallback(
        (onDeselect: () => void, selectedCount: number, selectedBills: IBaseEntity[]) => {
            return (
                <BillApprovalQueueFloatingActionBar
                    selectedCount={selectedCount}
                    onDeselect={onDeselect}
                    onApprove={() => void handleBulkApprove(selectedBills, onDeselect)}
                    disabled={actionBeingPerformed !== undefined}
                />
            );
        },
        [handleBulkApprove, actionBeingPerformed],
    );

    useEffect(() => {
        void handleReload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // In webview mode, refresh when pathname changes (e.g., back button from bill detail)
    useEffect(() => {
        if (webview) {
            void handleReload();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const modalConfig = {
        parentRoute: match.url,
        beforeClose: handleBeforeClose,
    };

    const backToBillsLinkContent = (
        <span className="inline-flex items-center gap-1">
            <BdsIconArrowLeft testid="backToBillsArrow" size="sm" />
            Back to Bills
        </span>
    );

    const renderHeader = () => {
        if (newBTListHeader) {
            return (
                <BTListHeader testid="billApprovalQueueHeader" ariaLabel="Bills to approve">
                    <div className="flex flex-col gap-2">
                        <div>
                            <BTListHeader.ActionButtons
                                testid="billApprovalQueueHeaderActionButtons"
                                iconActions={[
                                    {
                                        icon: "help",
                                        onClick: () => {
                                            window.open(
                                                "https://helpcenter.buildertrend.net/s/article/Purchase-Orders-Bills-Overview",
                                                "_blank",
                                                "noopener,noreferrer",
                                            );
                                        },
                                    },
                                ]}
                            />
                            <BTListHeader.Title>Bills to approve</BTListHeader.Title>
                        </div>
                        <BdsLink
                            testid="backToBillsLink"
                            onClick={handleBackToBills}
                            className="bds-link--back bds-link--gray self-start"
                        >
                            {backToBillsLinkContent}
                        </BdsLink>
                    </div>
                </BTListHeader>
            );
        } else {
            return (
                <BdsListPageHeader
                    id="billApprovalQueueHeader"
                    testid="billApprovalQueueHeader"
                    title="Bills to approve"
                    iconActions={[
                        {
                            icon: "help",
                            onClick: () => {
                                window.open(
                                    "https://helpcenter.buildertrend.net/s/article/Purchase-Orders-Bills-Overview",
                                    "_blank",
                                    "noopener,noreferrer",
                                );
                            },
                        },
                    ]}
                    breadcrumbs={[
                        {
                            id: "backToBillsLink",
                            testid: "backToBillsLink",
                            children: backToBillsLinkContent,
                            ariaLabel: "Back to Bills",
                            value: "backToBills",
                            onClick: handleBackToBills,
                        },
                    ]}
                    breadcrumbTestid="billApprovalQueueBreadcrumbs"
                    primaryButtonProps={{ id: "", isHidden: true }}
                />
            );
        }
    };

    return (
        <BillLinkAbsoluteContext.Provider value={getBillViewModePreference() === "fullPage"}>
            <BTListLayoutBDS
                title="Bills to approve"
                className="BillApprovalQueueLayout"
                renderWithActions={() => {
                    return (
                        <>
                            <RouteBill
                                modalConfig={modalConfig}
                                onApproveAndNext={handleApproveAndNext}
                            />
                            <ListSection>{renderHeader()}</ListSection>
                            {gridState.dataState === "loading" && <BTLoading />}

                            {!gridEntity ? (
                                <BTListLayoutWhitespace />
                            ) : (
                                <>
                                    {!gridEntity.listMetadata.hasData ? (
                                        <BTListLayoutWhitespace>
                                            <BillApprovalQueueEmptyState
                                                onBackToBills={handleBackToBills}
                                            />
                                        </BTListLayoutWhitespace>
                                    ) : (
                                        <ListSection>
                                            <GridContainer
                                                autoHeaderHeight
                                                gridState={gridState.dataState}
                                                data={gridState.data}
                                                columnsList={gridState.columnsList}
                                                columnsListAll={gridEntity.columnsListAll}
                                                sortingData={gridState.sortingData}
                                                footerData={gridState.footerData}
                                                onColumnResize={() => {}}
                                                onColumnReorder={() => {}}
                                                onColumnFrozen={() => {}}
                                                hasSelectableRows={true}
                                                renderFab={renderFab}
                                                onSortChanged={handleSort}
                                                showToolbar={false}
                                                canReorderColumns={false}
                                                canFreezeColumns={false}
                                                entityType={entityType}
                                                gridViews={[]}
                                                selectedGridView={undefined!}
                                            />

                                            <BTListLayoutWhitespace />
                                            <ListActionBar sticky="bottom">
                                                <BTRow justify="end" align="middle">
                                                    <BTCol>
                                                        {gridEntity.pagingData && (
                                                            <BTPagination
                                                                id="billApprovalQueuePagination"
                                                                entityType={entityType}
                                                                pagingData={gridEntity.pagingData}
                                                                onChange={handlePageChange}
                                                            />
                                                        )}
                                                    </BTCol>
                                                </BTRow>
                                            </ListActionBar>
                                        </ListSection>
                                    )}
                                </>
                            )}
                        </>
                    );
                }}
            />
        </BillLinkAbsoluteContext.Provider>
    );
};

export const BillApprovalQueue = BillApprovalQueueInternal;
